import type { ApiRegisterUserJourneyEventParams } from '~/handlers/ports/api-register-user-journey-event.port';
import {
  registerUserJourneyEventHandler,
  UserJourneyEvent,
} from '~/handlers/register-user-journey-event';
import { useMarketingTrackingStore } from '~/stores/apostatudo/marketing-tracking';
import { useDeviceStore } from '~/stores/apostatudo/device';
import { useUserJourneyStore } from '~/stores/apostatudo/user-journey';
import isDeepEqual from '~/utils/apostatudo/is-deep-equal';
import { tryCatch } from '~/utils/apostatudo/try-catch';

type UserJourneyPayload = Omit<ApiRegisterUserJourneyEventParams, 'event'>;

export const useUserJourney = () => {
  const {
    user,
    setUser,
    device,
    setDevice,
    utm,
    setUtm,
    google,
    setGoogle,
    facebook,
    setFacebook,
    tiktok,
    setTiktok,
    apt,
    setApt,
  } = useUserJourneyStore();

  const deviceStore = useDeviceStore();
  const { fingerprint } = storeToRefs(deviceStore);

  const marketingTrackingStore = useMarketingTrackingStore();
  const {
    utm: marketingUtm,
    google: marketingGoogle,
    facebook: marketingFacebook,
    tiktok: marketingTiktok,
    apt: marketingApt,
  } = storeToRefs(marketingTrackingStore);

  const { userId } = useUserData();

  // Detecta bots para evitar delay desnecessário que afeta LCP
  const { isBot } = usePerformanceBotBypass();

  const sanitizeObject = <T extends Record<string, unknown>>(
    objectToClean?: T,
  ) => {
    if (!objectToClean) {
      return {} as Partial<T>;
    }

    return Object.entries(objectToClean).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc;
      }

      acc[key as keyof T] = value as T[keyof T];

      return acc;
    }, {} as Partial<T>);
  };

  const sanitizePayload = (payload: UserJourneyPayload) => {
    const sanitizedPayload: UserJourneyPayload = {};

    if (payload.user?.id) {
      sanitizedPayload.user = { id: payload.user.id };
    }

    if (payload.device?.fingerprint) {
      sanitizedPayload.device = { fingerprint: payload.device.fingerprint };
    }

    const cleanedUtm = sanitizeObject(payload.utm);

    if (Object.keys(cleanedUtm).length) {
      sanitizedPayload.utm = cleanedUtm;
    }

    const cleanedGoogle = sanitizeObject(payload.google);

    if (Object.keys(cleanedGoogle).length) {
      sanitizedPayload.google = cleanedGoogle;
    }

    const cleanedFacebook = sanitizeObject(payload.facebook);

    if (Object.keys(cleanedFacebook).length) {
      sanitizedPayload.facebook = cleanedFacebook;
    }

    const cleanedTiktok = sanitizeObject(payload.tiktok);

    if (Object.keys(cleanedTiktok).length) {
      sanitizedPayload.tiktok = cleanedTiktok;
    }

    const cleanedApt = sanitizeObject(payload.apt);

    if (Object.keys(cleanedApt).length) {
      sanitizedPayload.apt = cleanedApt;
    }

    return sanitizedPayload;
  };

  const omitNullAndEmptyObjects = (payload: UserJourneyPayload) => {
    const cleanPayload: UserJourneyPayload = {};

    if (payload.user && Object.keys(payload.user).length) {
      cleanPayload.user = payload.user;
    }

    if (payload.device?.fingerprint) {
      cleanPayload.device = payload.device;
    }

    if (typeof payload.deposit_id === 'number') {
      cleanPayload.deposit_id = payload.deposit_id;
    }

    if (payload.utm && Object.keys(payload.utm).length) {
      cleanPayload.utm = payload.utm;
    }

    if (payload.google && Object.keys(payload.google).length) {
      cleanPayload.google = payload.google;
    }

    if (payload.facebook && Object.keys(payload.facebook).length) {
      cleanPayload.facebook = payload.facebook;
    }

    if (payload.tiktok && Object.keys(payload.tiktok).length) {
      cleanPayload.tiktok = payload.tiktok;
    }

    if (payload.apt && Object.keys(payload.apt).length) {
      cleanPayload.apt = payload.apt;
    }

    if (payload.slug) {
      cleanPayload.slug = payload.slug;
    }

    return cleanPayload;
  };

  const getEnterSiteUtmPayload = (): UserJourneyPayload['utm'] => {
    const normalizedUtm = sanitizeObject(marketingUtm.value);

    if (Object.keys(normalizedUtm).length) {
      return normalizedUtm;
    }

    return undefined;
  };

  const getEnterSiteGooglePayload = (): UserJourneyPayload['google'] => {
    const normalizedGoogle = sanitizeObject(marketingGoogle.value);

    if (Object.keys(normalizedGoogle).length) {
      return normalizedGoogle;
    }

    return undefined;
  };

  const getEnterSiteFacebookPayload = (): UserJourneyPayload['facebook'] => {
    const normalizedFacebook = sanitizeObject(marketingFacebook.value);

    if (Object.keys(normalizedFacebook).length) {
      return normalizedFacebook;
    }

    return undefined;
  };

  const getEnterSiteTiktokPayload = (): UserJourneyPayload['tiktok'] => {
    const normalizedTiktok = sanitizeObject(marketingTiktok.value);

    if (Object.keys(normalizedTiktok).length) {
      return normalizedTiktok;
    }

    return undefined;
  };

  const getEnterSiteAptPayload = (): UserJourneyPayload['apt'] => {
    const normalizedApt = sanitizeObject(marketingApt.value);

    if (Object.keys(normalizedApt).length) {
      return normalizedApt;
    }

    return undefined;
  };

  const getEnterSiteDevicePayload = (): UserJourneyPayload['device'] => {
    if (fingerprint.value) {
      return { fingerprint: fingerprint.value };
    }

    return undefined;
  };

  const getEnterSiteUserPayload = (): UserJourneyPayload['user'] => {
    if (userId.value) {
      return { id: String(userId.value) };
    }

    return undefined;
  };

  const buildEnterSitePayload = (): UserJourneyPayload => {
    return sanitizePayload({
      user: getEnterSiteUserPayload(),
      device: getEnterSiteDevicePayload(),
      utm: getEnterSiteUtmPayload(),
      google: getEnterSiteGooglePayload(),
      facebook: getEnterSiteFacebookPayload(),
      tiktok: getEnterSiteTiktokPayload(),
      apt: getEnterSiteAptPayload(),
    });
  };

  const getStoredPayload = (): UserJourneyPayload => {
    return sanitizePayload({
      user: user.id ? { id: user.id } : undefined,
      device: device.fingerprint
        ? { fingerprint: device.fingerprint }
        : undefined,
      utm,
      google,
      facebook,
      tiktok,
      apt,
    });
  };

  const persistPayload = (payload: UserJourneyPayload) => {
    setUser(payload.user ?? {});

    setDevice(payload.device ?? {});

    setUtm(payload.utm ?? {});

    setGoogle(payload.google ?? {});

    setFacebook(payload.facebook ?? {});

    setTiktok(payload.tiktok ?? {});

    setApt(payload.apt ?? {});
  };

  const sendEvent = async (
    event: UserJourneyEvent,
    payload: UserJourneyPayload,
    shouldPersist = false,
  ) => {
    // Bots não enviam user journey (melhora performance em testes)
    if (isBot.value) {
      return;
    }

    const cleanedPayload = omitNullAndEmptyObjects(payload);

    const [, registerUserJourneyEventError] = await tryCatch(
      registerUserJourneyEventHandler({
        event,
        ...cleanedPayload,
      }),
    );

    if (registerUserJourneyEventError) {
      console.error(
        '[registerUserJourneyEventHandler] Error registering user journey event:',
        registerUserJourneyEventError,
      );

      return;
    }

    if (shouldPersist) {
      persistPayload(cleanedPayload);
    }
  };

  const dispatchEvent = (
    event: UserJourneyEvent,
    payload: UserJourneyPayload,
  ) => {
    // Fire-and-forget to avoid blocking user flows; swallow errors
    queueMicrotask(async () => {
      try {
        await sendEvent(event, payload);
      } catch (error) {
        console.debug('[user-journey] ignored event error', error);
      }
    });
  };

  const enterSiteEvent = async () => {
    const payload = buildEnterSitePayload();
    const storedPayload = getStoredPayload();

    if (isDeepEqual(payload, storedPayload)) {
      return;
    }

    await sendEvent(UserJourneyEvent.ENTER_SITE, payload, true);
  };

  const registerUserEvent = () => {
    const payload = getStoredPayload();

    dispatchEvent(UserJourneyEvent.REGISTER_USER, payload);
  };

  const blogPostClickEvent = (slug: string) => {
    if (!slug) return;

    if (isBot.value) return;

    const payload: UserJourneyPayload = sanitizePayload({
      user: getEnterSiteUserPayload(),
      device: getEnterSiteDevicePayload(),
    });

    dispatchEvent(UserJourneyEvent.BLOG_POST_CLICK, { ...payload, slug });
  };

  const makeDepositEvent = (transactionId: string) => {
    const numericDepositId = transactionId?.match(/\d+/)?.[0];

    if (!numericDepositId) {
      return;
    }

    const depositId = Number(numericDepositId);

    if (Number.isNaN(depositId)) {
      return;
    }

    const payload: UserJourneyPayload = {
      ...getStoredPayload(),
      deposit_id: depositId,
    };

    dispatchEvent(UserJourneyEvent.MAKE_DEPOSIT, payload);
  };

  return {
    enterSiteEvent,
    registerUserEvent,
    makeDepositEvent,
    blogPostClickEvent,
  };
};
